/**
 * The org's vocabulary bindings: typed wrapper over `GET`/`PUT /v1/twin/vocabulary-bindings` and the defects a binding
 * may name, `GET /v1/twin/vocabulary-bindings/targets` (platform-core/internal/twin/vocab_bindings.go and
 * vocab_targets.go, migrations 0179 and 0191).
 *
 * A plant books scrap in its own words and binds each to a canonical defect, marks it out of scope, or marks it not
 * scrap. Beside the bindings the server files each approved defect binding's word under every key a page reads that
 * defect by (`words`), so a page names one booked defect with one word, the plant's own (lib/defect-words).
 *
 * A word the pack has no defect for can be bound to one of the org's own defect types (`/v1/twin/defect-types`,
 * org_defect_types.go, migration 0265), which a person adds while mapping the word: a name, the area of the route and,
 * if they know it, the pack's defect family. Pages name such a type by its name (`words` carries it).
 */

import { api } from "@/lib/api-client";

/** The query key of the org's vocabulary bindings: whatever writes a binding invalidates it. */
export const VOCABULARY_BINDINGS_KEY = ["twin-vocabulary-bindings"] as const;

/** What a name means: bound to a canonical defect or parameter, out of scope for process correlation (machining,
 *  handling, bookkeeping), or not scrap (0191: a defect name that is no scrapped casting, out of every scrap figure;
 *  a defect name only). */
export type VocabScope = "bound" | "out_of_scope" | "not_scrap";

/** One of the org's names, bound to a canonical defect or parameter, out of scope, or not scrap. */
export interface VocabBinding {
  kind: "defect" | "parameter";
  /** The plant's name, as booked. */
  source_name: string;
  /** The name as the correlation engine reads it: lower case, spaces and hyphens folded to underscores. */
  source_key: string;
  scope: VocabScope;
  target?: string;
  status: "proposed" | "approved" | "rejected";
  reason?: string;
  updated_at: string;
}

export interface VocabBindingList {
  items: VocabBinding[];
  /** By every key a page reads a defect by (as booked, the global fold's, a binding's target, the analysis's), the
   *  plant's own word for it, from its approved defect bindings. Absent from an older server. */
  words?: Record<string, string>;
}

/** One binding as a PUT sets it: approved or rejected is the caller's decision, proposed awaits one. */
export interface VocabBindingInput {
  kind: "defect" | "parameter";
  /** The plant's name, as booked (at most 200 characters). */
  source_name: string;
  scope: VocabScope;
  /** The canonical defect or parameter: required when bound, absent otherwise. */
  target?: string;
  status: "proposed" | "approved" | "rejected";
  /** Why (at most 2000 characters). */
  reason?: string;
  /** What the binding rests on: a JSON object of at most 16 KiB. */
  evidence?: Record<string, unknown>;
}

/** A defect a binding may name, and the other names (as keys) that fold onto it ("blow" onto "blow_hole"). One of the
 *  org's own types carries its name and `own` (withOrgDefects puts them in the list; the server lists them apart). */
export interface VocabDefectTarget {
  key: string;
  aliases: string[];
  /** One of the org's own defect types: its name, as the person who added it wrote it. */
  name?: string;
  own?: boolean;
}

/** One of the org's own defect types (migration 0265). */
export interface OrgDefectType {
  /** From its name: lower case, every character other than a to z, 0 to 9 and the underscore folded to one. */
  key: string;
  name: string;
  /** The area of the route where it is usually caused (moulding_sand). */
  area: string;
  /** The pack's defect family it belongs to; absent when none was chosen, so the analysis tests it with no prior. */
  family?: string;
  severity?: DefectSeverity;
  created_by?: string;
  created_at: string;
}

export type DefectSeverity = "minor" | "major" | "critical";

/** An area or family a new defect type may name, with its words. */
export interface DefectTypeChoice {
  key: string;
  label: string;
}

/** A new defect type, as POST /v1/twin/defect-types takes it. */
export interface OrgDefectTypeInput {
  name: string;
  area: string;
  family?: string;
  severity?: DefectSeverity;
}

/** GET /v1/twin/vocabulary-bindings/targets. An older server sends `defects` alone. */
export interface VocabTargets {
  /** The pack's defects (and, once read through withOrgDefects, the org's own types after them). */
  defects: VocabDefectTarget[];
  org_defects?: OrgDefectType[];
  areas?: DefectTypeChoice[];
  families?: DefectTypeChoice[];
}

/** The targets with the org's own defect types in the list a binding may name, after the pack's, each with its name and
 *  marked as the org's: every picker that lists `defects` offers them. A key the pack lists already is not added
 *  twice. Pure. */
export function withOrgDefects(t: VocabTargets): VocabTargets {
  const own = (t.org_defects ?? []).filter((d) => typeof d?.key === "string" && d.key !== "");
  if (own.length === 0) return t;
  const listed = new Set((t.defects ?? []).map((d) => d.key));
  return {
    ...t,
    defects: [
      ...(t.defects ?? []),
      ...own.filter((d) => !listed.has(d.key)).map((d) => ({ key: d.key, aliases: [], name: d.name, own: true })),
    ],
  };
}

/** The query key of the defects a binding may name: adding a defect type invalidates it. */
export const VOCABULARY_TARGETS_KEY = ["twin-vocabulary-targets"] as const;

export const twinVocabulary = {
  /** Every binding of the org, and its defect words. */
  list: () => api.get<VocabBindingList>("/v1/twin/vocabulary-bindings"),
  /** Sets a batch of bindings in one transaction, all or nothing (model write). */
  put: (bindings: VocabBindingInput[]) =>
    api.put<{ items: VocabBinding[] }>("/v1/twin/vocabulary-bindings", { bindings }),
  /** The defects a binding may name: the priors', the same for every org, and the org's own types apart. */
  targets: () => api.get<VocabTargets>("/v1/twin/vocabulary-bindings/targets"),
};

export const twinDefectTypes = {
  /** The org's own defect types, and the areas and families a new one may name. */
  list: () =>
    api.get<{ items: OrgDefectType[]; areas: DefectTypeChoice[]; families: DefectTypeChoice[] }>("/v1/twin/defect-types"),
  /** Adds one (model write). 409 defect_type_taken when the pack or the org already has a defect of its key. */
  create: (input: OrgDefectTypeInput) => api.post<{ item: OrgDefectType }>("/v1/twin/defect-types", input),
};
